import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import {
  Button,
  cn,
  Avatar,
  AvatarFallback,
  AvatarImage,
  Badge,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@pantry/ui';
import {
  useAdminUserDetail,
  useUpdateUserRole,
  useCurrentUser,
  type AdminUser,
} from '@pantry/api-client';
import { APP_ROLES, type AppRole, hasAppPermission } from '@pantry/shared';
import {
  XMarkIcon,
  HomeIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { toast } from 'sonner';

function getRoleLabels(t: ReturnType<typeof useLingui>['t']): Record<AppRole, string> {
  return {
    superadmin: t`Super Admin`,
    admin: t`Admin`,
    user: t`User`,
  };
}

function getRoleDescriptions(t: ReturnType<typeof useLingui>['t']): Record<AppRole, string> {
  return {
    superadmin: t`Full system access including user management and settings`,
    admin: t`Can view metrics and manage content`,
    user: t`Standard user, no admin access`,
  };
}

const ROLE_COLORS: Record<AppRole, string> = {
  superadmin: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
  admin: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  user: 'bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300',
};

function formatRelativeTime(
  date: Date,
  t: ReturnType<typeof useLingui>['t'],
  locale: string
): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return t`Just now`;
  if (diffMins < 60) return t`${diffMins}m ago`;
  if (diffHours < 24) return t`${diffHours}h ago`;
  // i18n: "d" abbreviates "days", relative time
  if (diffDays < 7) return t`${diffDays}d ago`;
  if (diffDays < 30) return t`${Math.floor(diffDays / 7)}w ago`;
  return date.toLocaleDateString(locale, { month: 'short', day: 'numeric' });
}

interface UserDetailsSheetProps {
  user: AdminUser;
  onClose: () => void;
}

export function UserDetailsSheet({ user, onClose }: UserDetailsSheetProps) {
  const { t, i18n } = useLingui();
  const ROLE_LABELS = getRoleLabels(t);

  const getHouseholdRoleLabel = (role: string): string => {
    switch (role) {
      case 'owner':
        return t`Owner`;
      case 'admin':
        return t`Admin`;
      case 'member':
        return t`Member`;
      default:
        return role;
    }
  };
  const { data: currentUser } = useCurrentUser();
  const { data: userDetail, isLoading } = useAdminUserDetail(user.id);
  const [showRoleDialog, setShowRoleDialog] = useState(false);

  const initials = user.name
    ? user.name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2)
    : user.email[0].toUpperCase();

  // Check if current user can change roles
  const canChangeRole = currentUser && hasAppPermission(currentUser.appRole, 'app:users:edit');
  // Superadmins can't have their role changed by non-superadmins
  const canChangeThisUserRole =
    canChangeRole &&
    (currentUser?.appRole === 'superadmin' || user.appRole !== 'superadmin') &&
    user.id !== currentUser?.id; // Can't change own role

  return (
    <>
      <div className="fixed inset-0 z-50 bg-black/50" onClick={onClose}>
        <div
          className="fixed right-0 top-0 h-full w-full max-w-md bg-card border-l border-border shadow-xl animate-in slide-in-from-right duration-200 overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between z-10">
            <h2 className="text-lg font-semibold">
              <Trans>User Details</Trans>
            </h2>
            <Button variant="ghost" size="icon" onClick={onClose} className="rounded-lg">
              <XMarkIcon className="h-5 w-5" />
              <span className="sr-only">
                <Trans>Close</Trans>
              </span>
            </Button>
          </div>

          <div className="p-6">
            {/* User header */}
            <div className="flex items-center gap-4 mb-6">
              <Avatar className="h-16 w-16">
                {user.avatarUrl && (
                  <AvatarImage src={user.avatarUrl} alt={user.name || user.email} />
                )}
                <AvatarFallback className="text-lg font-medium">{initials}</AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="font-medium text-foreground truncate">{user.name || t`No name`}</p>
                  {user.isRestricted && (
                    <ExclamationTriangleIcon className="h-4 w-4 text-amber-500 flex-shrink-0" />
                  )}
                </div>
                <p className="text-sm text-muted-foreground truncate">{user.email}</p>
              </div>
            </div>

            {/* Account Info */}
            <section className="mb-6">
              <h3 className="text-sm font-medium text-muted-foreground mb-3">
                <Trans>Account Info</Trans>
              </h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between items-center py-2 border-b border-border/50">
                  <span className="text-muted-foreground">
                    <Trans>Role</Trans>
                  </span>
                  <div className="flex items-center gap-2">
                    <Badge
                      variant="secondary"
                      className={cn('font-medium', ROLE_COLORS[user.appRole])}
                    >
                      {ROLE_LABELS[user.appRole]}
                    </Badge>
                    {canChangeThisUserRole && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowRoleDialog(true)}
                        className="h-6 px-2 text-xs"
                      >
                        <Trans>Change</Trans>
                      </Button>
                    )}
                  </div>
                </div>
                <div className="flex justify-between py-2 border-b border-border/50">
                  <span className="text-muted-foreground">
                    <Trans>Status</Trans>
                  </span>
                  <span className={user.isRestricted ? 'text-amber-500' : 'text-green-600'}>
                    {user.isRestricted ? t`Restricted` : t`Active`}
                  </span>
                </div>
                <div className="flex justify-between py-2 border-b border-border/50">
                  <span className="text-muted-foreground">
                    <Trans>Joined</Trans>
                  </span>
                  <span>{new Date(user.createdAt).toLocaleDateString(i18n.locale)}</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-muted-foreground">
                    <Trans>Last login</Trans>
                  </span>
                  <span>
                    {user.lastLoginAt
                      ? formatRelativeTime(new Date(user.lastLoginAt), t, i18n.locale)
                      : t`Never`}
                  </span>
                </div>
              </div>
            </section>

            {/* Quick Stats */}
            <section className="mb-6">
              <h3 className="text-sm font-medium text-muted-foreground mb-3">
                <Trans>Quick Stats</Trans>
              </h3>
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-lg bg-muted/50 p-3 text-center">
                  <p className="text-2xl font-semibold">{user.householdCount}</p>
                  <p className="text-xs text-muted-foreground">
                    <Trans>Households</Trans>
                  </p>
                </div>
                <div className="rounded-lg bg-muted/50 p-3 text-center">
                  <p className="text-2xl font-semibold">
                    {user.itemCount.toLocaleString(i18n.locale)}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    <Trans>Items</Trans>
                  </p>
                </div>
              </div>
            </section>

            {/* Households */}
            <section>
              <h3 className="text-sm font-medium text-muted-foreground mb-3">
                <Trans>Households ({userDetail?.households?.length || user.householdCount})</Trans>
              </h3>
              {isLoading ? (
                <div className="flex justify-center py-4">
                  <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                </div>
              ) : userDetail?.households && userDetail.households.length > 0 ? (
                <div className="space-y-2">
                  {userDetail.households.map((household) => (
                    <div
                      key={household.id}
                      className="flex items-center gap-3 rounded-lg bg-muted/30 p-3"
                    >
                      <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-muted">
                        <HomeIcon className="h-4 w-4 text-muted-foreground" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{household.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {getHouseholdRoleLabel(household.role)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">
                  <Trans>No households</Trans>
                </p>
              )}
            </section>
          </div>
        </div>
      </div>

      {/* Role Change Dialog */}
      {showRoleDialog && (
        <ChangeRoleDialog
          user={user}
          currentUserRole={currentUser?.appRole || 'user'}
          onClose={() => setShowRoleDialog(false)}
        />
      )}
    </>
  );
}

interface ChangeRoleDialogProps {
  user: AdminUser;
  currentUserRole: AppRole;
  onClose: () => void;
}

function ChangeRoleDialog({ user, currentUserRole, onClose }: ChangeRoleDialogProps) {
  const { t } = useLingui();
  const ROLE_LABELS = getRoleLabels(t);
  const ROLE_DESCRIPTIONS = getRoleDescriptions(t);
  const [selectedRole, setSelectedRole] = useState<AppRole>(user.appRole);
  const updateRole = useUpdateUserRole();

  // Only superadmins can assign superadmin role
  const availableRoles = APP_ROLES.filter((role) => {
    if (role === 'superadmin' && currentUserRole !== 'superadmin') return false;
    return true;
  });

  const handleSubmit = async () => {
    if (selectedRole === user.appRole) {
      onClose();
      return;
    }

    try {
      await updateRole.mutateAsync({
        userId: user.id,
        data: { role: selectedRole },
      });
      toast.success(t`Role changed to ${ROLE_LABELS[selectedRole]}`);
      onClose();
    } catch {
      toast.error(t`Failed to change role`);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            <Trans>Change User Role</Trans>
          </DialogTitle>
          <DialogDescription>
            <Trans>
              Changing role for <strong>{user.name || user.email}</strong>
            </Trans>
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <p className="text-sm text-muted-foreground mb-3">
            <Trans>Current role:</Trans>{' '}
            <Badge
              variant="secondary"
              className={cn('font-medium ml-1', ROLE_COLORS[user.appRole])}
            >
              {ROLE_LABELS[user.appRole]}
            </Badge>
          </p>

          <p className="text-sm font-medium mb-2">
            <Trans>Select new role:</Trans>
          </p>
          <div className="space-y-2">
            {availableRoles.map((role) => (
              <button
                key={role}
                onClick={() => setSelectedRole(role)}
                className={cn(
                  'w-full flex items-start gap-3 rounded-lg border p-3 text-left transition-colors',
                  selectedRole === role
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-primary/50'
                )}
              >
                <div
                  className={cn(
                    'flex h-5 w-5 items-center justify-center rounded-full border-2 mt-0.5',
                    selectedRole === role
                      ? 'border-primary bg-primary'
                      : 'border-muted-foreground/50'
                  )}
                >
                  {selectedRole === role && (
                    <div className="h-2 w-2 rounded-full bg-primary-foreground" />
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{ROLE_LABELS[role]}</span>
                    {role === 'superadmin' && (
                      <ShieldCheckIcon className="h-4 w-4 text-purple-500" />
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">{ROLE_DESCRIPTIONS[role]}</p>
                </div>
              </button>
            ))}
          </div>

          <div className="mt-4 flex items-start gap-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 p-3 text-amber-800 dark:text-amber-200">
            <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0 mt-0.5" />
            <p className="text-sm">
              <Trans>This action will be logged in the audit trail.</Trans>
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            <Trans>Cancel</Trans>
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={updateRole.isPending || selectedRole === user.appRole}
          >
            {updateRole.isPending ? <Trans>Saving...</Trans> : <Trans>Save Changes</Trans>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
